import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SupportDialogComponent } from 'app/core/components/popup/support-dialog/support-dialog.component';
import { environment } from 'shared/environments/environment';
import { DashboardService } from './dashboard.service';
import { EntityService } from './entity.service';
import { ProductService } from './product.service';
import { copySupportContext, supportPageUrl } from './support-context.utils';

@Injectable({ providedIn: 'root' })
export class SupportService {
    private opening = false;
    private pageContexts = new Set<{
        active: () => boolean;
        read: () => Record<string, unknown>;
    }>();

    constructor(
        private modal: NgbModal,
        private router: Router,
        private dashboard: DashboardService,
        private entities: EntityService,
        private product: ProductService,
        @Inject(DOCUMENT) private document: Document
    ) {}

    /** Dashboards with local filters can register a reader and unregister it on destruction. */
    public registerPageContext(
        read: () => Record<string, unknown>,
        active?: () => boolean
    ): () => void {
        const navigation = this.router.getCurrentNavigation();
        const route = (navigation?.finalUrl?.toString() || this.router.url).split(
            /[?#]/
        )[0];
        const registration = {
            read,
            active: active || ((): boolean => this.router.url.split(/[?#]/)[0] === route),
        };
        this.pageContexts.add(registration);
        return () => {
            this.pageContexts.delete(registration);
        };
    }

    public captureContext(): Record<string, unknown> {
        const browser = this.document.defaultView;
        const route = this.router.url.split(/[?#]/)[0];
        const hotelIds = [
            ...new Set(this.dashboard.selectedHtlId || this.entities.selectedHtlId || []),
        ];
        const selected = new Set(hotelIds.map(String));
        let pageOptions: Record<string, unknown> = {};
        for (const context of this.pageContexts) {
            if (context.active()) {
                try {
                    pageOptions = { ...pageOptions, ...context.read() };
                } catch {
                    /* Other readers and shared context remain available. */
                }
            }
        }
        return copySupportContext({
            page_url: supportPageUrl(browser?.location.href || ''),
            route,
            dashboard_id: this.dashboard.id,
            dashboard_title: this.document.title,
            product: this.product.product,
            environment: (environment as { name?: string }).name,
            captured_at: new Date().toISOString(),
            hotel_ids: hotelIds,
            hotels: (this.entities.entities || [])
                .filter(hotel => selected.has(String(hotel.htl_id)))
                .slice(0, 200)
                .map(hotel => ({
                    id: hotel.htl_id,
                    name: hotel.long_name || hotel.name,
                })),
            selection_labels: this.entities.selectedEntityLabel,
            ...this.dashboard.supportContext,
            page_options: pageOptions,
            browser: browser?.navigator.userAgent,
            viewport: { width: browser?.innerWidth, height: browser?.innerHeight },
        });
    }

    public open(): void {
        if (this.opening) return;
        this.opening = true;
        try {
            const context = this.captureContext();
            context.page_screenshot_status = 'not_requested';
            const ref = this.modal.open(SupportDialogComponent, {
                size: 'lg',
                centered: true,
                backdrop: 'static',
                keyboard: false,
                windowClass: 'pulse-support-modal',
                backdropClass: 'pulse-support-backdrop',
                ariaLabelledBy: 'pulse-support-title',
            });
            ref.componentInstance.context = context;
            // Pre-fill SITA (htlcd) only when exactly one hotel is selected.
            const hotelIds = context.hotel_ids as unknown[];
            if (hotelIds?.length === 1) {
                const hotel = (this.entities.entities || []).find(
                    entity => String(entity.htl_id) === String(hotelIds[0])
                );
                if (hotel?.id) ref.componentInstance.sita = String(hotel.id);
            }
            ref.result.then(
                () => {
                    this.opening = false;
                },
                () => {
                    this.opening = false;
                }
            );
        } catch (error) {
            this.opening = false;
            throw error;
        }
    }
}
